import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { API_URL } from "../../apiConfig";
import {
  computeBundleLineTotal,
  enrichBundlePickRow,
  getEligibleProductsForOffer,
  getVisibleAddonsForProductPick,
  normalizePickSlots,
  offerUsesPickSlots,
  slotOptionSurcharge,
  slotOptionRequiredSize,
  validateBundlePicks,
} from "../../utils/bundleOffers";
import { normalizeOptionList, sortOptionListByPrice } from "../../utils/variantStock";

function productLabel(p) {
  return (p?.name_ar || p?.name || "").trim() || "منتج";
}

function mapEligibleApiProduct(p) {
  return {
    ...p,
    price: p.base_price ?? p.price,
    name: p.name_ar || p.name,
    displayImage: p.image || (p.images && p.images[0]),
    addons: p.addons || p.product_addons || [],
    product_addons: p.addons || p.product_addons || [],
  };
}

function formatSurcharge(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "+₪0.00";
  return `+₪${v.toFixed(2)}`;
}

export default function BundleOfferPickerModal({
  open,
  offer,
  catalogId,
  products,
  productsById,
  primaryColor,
  secondaryColor,
  onClose,
  onConfirm,
}) {
  const slots = useMemo(() => normalizePickSlots(offer?.pick_slots), [offer?.pick_slots]);
  const usesSlots = offerUsesPickSlots(offer);
  const pickCount = usesSlots ? slots.length : Math.max(1, Number(offer?.pick_count) || 1);
  const legacyReqSize = String(offer?.required_size || "").trim();

  const [picks, setPicks] = useState([]);
  const [error, setError] = useState("");
  const [eligibleProducts, setEligibleProducts] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);

  useEffect(() => {
    if (!open || !offer) return;
    setPicks(
      Array.from({ length: pickCount }, (_, i) => ({
        product_id: "",
        selected_size: "",
        selected_addons: [],
        slot_surcharge: null,
      }))
    );
    setError("");
  }, [open, offer?.id, pickCount, legacyReqSize, usesSlots, slots]);

  useEffect(() => {
    if (!open || !offer?.id) {
      setEligibleProducts([]);
      return;
    }
    const ident = String(catalogId || "").trim();
    let cancelled = false;

    const slotProductIds = usesSlots
      ? [...new Set(slots.flatMap((s) => (s.options || []).map((o) => String(o.product_id))))]
      : [];

    const load = async () => {
      setLoadingEligible(true);
      try {
        if (ident) {
          const res = await fetch(
            `${API_URL}/catalog/${encodeURIComponent(ident)}/bundle-offers/${encodeURIComponent(offer.id)}/eligible-products`
          );
          if (res.ok) {
            const data = await res.json();
            let list = Array.isArray(data?.products)
              ? data.products.map(mapEligibleApiProduct)
              : [];
            if (usesSlots && slotProductIds.length) {
              const allowed = new Set(slotProductIds);
              list = list.filter((p) => allowed.has(String(p.id)));
            }
            if (!cancelled) {
              setEligibleProducts(list);
              return;
            }
          }
        }
        if (!cancelled) {
          let list = getEligibleProductsForOffer(offer, products);
          if (usesSlots && slotProductIds.length) {
            const allowed = new Set(slotProductIds);
            list = list.filter((p) => allowed.has(String(p.id)));
          }
          setEligibleProducts(list);
        }
      } catch {
        if (!cancelled) {
          let list = getEligibleProductsForOffer(offer, products);
          if (usesSlots && slotProductIds.length) {
            const allowed = new Set(slotProductIds);
            list = list.filter((p) => allowed.has(String(p.id)));
          }
          setEligibleProducts(list);
        }
      } finally {
        if (!cancelled) setLoadingEligible(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, offer?.id, catalogId, offer, products, usesSlots, slots]);

  const mergedProductsById = useMemo(() => {
    const map = new Map(productsById || []);
    for (const p of eligibleProducts) {
      map.set(String(p.id), p);
    }
    for (const p of products || []) {
      map.set(String(p.id), p);
    }
    return map;
  }, [productsById, eligibleProducts, products]);

  const eligible = useMemo(() => {
    if (eligibleProducts.length) return eligibleProducts;
    return getEligibleProductsForOffer(offer, products);
  }, [eligibleProducts, offer, products]);

  const total = useMemo(
    () => computeBundleLineTotal(offer, picks, mergedProductsById),
    [offer, picks, mergedProductsById]
  );

  if (!open || !offer) return null;

  const updatePick = (idx, patch) => {
    setPicks((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const resolvePickSize = (product, slotReqSize) => {
    const sizes = normalizeOptionList(product?.sizes);
    if (!sizes.length) return "";
    if (slotReqSize) return slotReqSize;
    return sizes.length === 1 ? sizes[0] : "";
  };

  const selectSlotOption = (idx, productId, surcharge, slot) => {
    const product = mergedProductsById.get(String(productId));
    const optSize = slotOptionRequiredSize(slot, productId);
    updatePick(idx, {
      product_id: productId,
      selected_addons: [],
      selected_size: resolvePickSize(product, optSize),
      slot_surcharge: surcharge,
    });
  };

  const toggleAddon = (idx, addon, checked) => {
    setPicks((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const cur = Array.isArray(row.selected_addons) ? row.selected_addons : [];
        const aid = String(addon.id);
        let next;
        if (checked) {
          next = cur.some((a) => String(a.id) === aid)
            ? cur
            : [...cur, { id: aid, name: addon.name, price: addon.price }];
        } else {
          next = cur.filter((a) => String(a.id) !== aid);
        }
        return { ...row, selected_addons: next };
      })
    );
  };

  const renderPickExtras = (idx, pick, slotReqSize) => {
    const product = mergedProductsById.get(String(pick.product_id));
    const sizes = sortOptionListByPrice(
      normalizeOptionList(product?.sizes),
      product?.extra_prices,
      product?.price
    );
    if (!sizes.length) {
      const addonsNoSize = product ? getVisibleAddonsForProductPick(product, "") : [];
      const selectedAddonIds = new Set((pick.selected_addons || []).map((a) => String(a.id)));
      if (!addonsNoSize.length) return null;
      return (
        <div className="bundle-offer-addons">
          <div className="bundle-offer-addons__label">إضافات (اختياري)</div>
          {addonsNoSize.map((ad) => (
            <label key={ad.id} className="bundle-offer-addon-row">
              <input
                type="checkbox"
                checked={selectedAddonIds.has(String(ad.id))}
                onChange={(e) => toggleAddon(idx, ad, e.target.checked)}
              />
              <span>{ad.name}</span>
              <span className="bundle-offer-addon-price">+₪{ad.price}</span>
            </label>
          ))}
        </div>
      );
    }

    const sizeVal = pick.selected_size || slotReqSize || "";
    const addons = product ? getVisibleAddonsForProductPick(product, sizeVal) : [];
    const selectedAddonIds = new Set((pick.selected_addons || []).map((a) => String(a.id)));

    return (
      <>
        {slotReqSize ? (
          <div className="bundle-offer-size-tag">الحجم: {slotReqSize}</div>
        ) : (
          <select
            className="bundle-offer-select"
            value={pick.selected_size || ""}
            onChange={(e) =>
              updatePick(idx, { selected_size: e.target.value, selected_addons: [] })
            }
          >
            <option value="">— الحجم —</option>
            {sizes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}

        {addons.length > 0 ? (
          <div className="bundle-offer-addons">
            <div className="bundle-offer-addons__label">إضافات (اختياري)</div>
            {addons.map((ad) => (
              <label key={ad.id} className="bundle-offer-addon-row">
                <input
                  type="checkbox"
                  checked={selectedAddonIds.has(String(ad.id))}
                  onChange={(e) => toggleAddon(idx, ad, e.target.checked)}
                />
                <span>{ad.name}</span>
                <span className="bundle-offer-addon-price">+₪{ad.price}</span>
              </label>
            ))}
          </div>
        ) : null}
      </>
    );
  };

  const renderSlotPick = (slot, idx) => {
    const pick = picks[idx] || {};
    const selectedPid = String(pick.product_id || "");

    return (
      <div key={idx} className="bundle-offer-slot-block">
        <div className="bundle-offer-slot-block__head">
          <div className="bundle-offer-slot-block__title">
            {slot.label || `العنصر ${idx + 1}`}
            <span className="bundle-offer-slot-block__req">*</span>
          </div>
          <span className="bundle-offer-slot-block__badge">مطلوب</span>
        </div>
        <div className="bundle-offer-slot-block__options">
          {(slot.options || []).map((opt) => {
            const pid = String(opt.product_id);
            const p = mergedProductsById.get(pid);
            const surcharge = slotOptionSurcharge(slot, pid);
            const checked = selectedPid === pid;
            return (
              <label
                key={pid}
                className={`bundle-offer-slot-option${checked ? " is-selected" : ""}`}
              >
                <input
                  type="radio"
                  name={`bundle-slot-${offer.id}-${idx}`}
                  checked={checked}
                  onChange={() => selectSlotOption(idx, pid, surcharge, slot)}
                />
                <span className="bundle-offer-slot-option__name">
                  {p ? productLabel(p) : pid}
                </span>
                <span className="bundle-offer-slot-option__price">{formatSurcharge(surcharge)}</span>
              </label>
            );
          })}
        </div>
        {selectedPid
          ? renderPickExtras(idx, pick, slotOptionRequiredSize(slot, selectedPid))
          : null}
      </div>
    );
  };

  const renderLegacyPick = (idx) => {
    const pick = picks[idx] || {};
    const product = mergedProductsById.get(String(pick.product_id));

    return (
      <div key={idx} className="bundle-offer-pick-block">
        <div className="bundle-offer-pick-block__title">المنتج {idx + 1}</div>
        <select
          className="bundle-offer-select"
          value={pick.product_id || ""}
          disabled={loadingEligible || eligible.length === 0}
          onChange={(e) => {
            const product = mergedProductsById.get(String(e.target.value));
            updatePick(idx, {
              product_id: e.target.value,
              selected_addons: [],
              selected_size: resolvePickSize(product, legacyReqSize),
              slot_surcharge: null,
            });
          }}
        >
          <option value="">— اختر منتج —</option>
          {eligible.map((p) => (
            <option key={p.id} value={p.id}>
              {productLabel(p)}
            </option>
          ))}
        </select>
        {product ? renderPickExtras(idx, pick, legacyReqSize) : null}
      </div>
    );
  };

  const handleConfirm = () => {
    try {
      const normalized = picks.map((p, i) => enrichBundlePickRow(p, offer, mergedProductsById, i));
      const missing = validateBundlePicks(offer, normalized, mergedProductsById);
      if (missing.length) {
        setError(missing.join("، "));
        return;
      }
      setError("");
      onConfirm({ offer, picks: normalized, unitPrice: total });
      onClose();
    } catch (err) {
      console.error("bundle offer confirm failed", err);
      setError("تعذر إضافة العرض — تأكد من اختيار جميع العناصر");
    }
  };

  return (
    <div className="bundle-offer-modal-backdrop" onClick={onClose}>
      <div
        className="bundle-offer-modal bundle-offer-modal--slots"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bundle-offer-modal__head">
          <div>
            <h3>{offer.name}</h3>
            {offer.description ? <p>{offer.description}</p> : null}
          </div>
          <button type="button" className="bundle-offer-modal__close" onClick={onClose} aria-label="إغلاق">
            <X size={20} />
          </button>
        </div>

        <div className="bundle-offer-modal__body">
          {loadingEligible ? (
            <p className="bundle-offer-modal__loading">جاري تحميل المنتجات…</p>
          ) : usesSlots && slots.every((s) => !s.options?.length) ? (
            <p className="bundle-offer-modal__error">لم يُضبط هذا العرض بعد — راجع إعدادات العناصر.</p>
          ) : !usesSlots && eligible.length === 0 ? (
            <p className="bundle-offer-modal__error">لا توجد منتجات متاحة لهذا العرض.</p>
          ) : null}

          {usesSlots
            ? slots.map((slot, idx) => renderSlotPick(slot, idx))
            : picks.map((_, idx) => renderLegacyPick(idx))}
        </div>

        {error ? <p className="bundle-offer-modal__error">{error}</p> : null}

        <div className="bundle-offer-modal__foot">
          <div className="bundle-offer-modal__total">₪{total}</div>
          <button
            type="button"
            className="catalog-btn bundle-offer-modal__cta"
            style={{ background: primaryColor, color: secondaryColor }}
            onClick={handleConfirm}
            disabled={loadingEligible}
          >
            أضف إلى السلة
          </button>
        </div>
      </div>
    </div>
  );
}
